import React, { useState, useEffect, useRef } from 'react';
import Alert from "@amzn/awsui-components-core/alert";
import Badge from "@amzn/awsui-components-core/badge";
import Box from "@amzn/awsui-components-core/box";
import Button from "@amzn/awsui-components-core/button";
import Checkbox from "@amzn/awsui-components-core/checkbox";
import Container from "@amzn/awsui-components-core/container";
import FormField from "@amzn/awsui-components-core/form-field";
import Icon from "@amzn/awsui-components-core/icon";
import Modal from "@amzn/awsui-components-core/modal";
import SpaceBetween from "@amzn/awsui-components-core/space-between";
import Spinner from "@amzn/awsui-components-core/spinner";
import ActionButtons from 'src/components/shared/ActionButtons';
import RichTextEditor from 'src/components/shared/RichTextEditor';
import RichTextContent from 'src/components/shared/RichTextContent';
import { fetchShiftPassdown, createShiftPassdown, updateShiftPassdown, deleteShiftPassdown, ShiftPassdownEntry, CreateShiftPassdownInput } from 'src/services/passdowns-api';
import { useUser } from 'src/context/UserContext';
import { saveDraft, loadDraft, clearDraft } from 'src/utils/formDraft';
import FormattedDate from 'src/components/shared/FormattedDate';
import { useAlertMessages } from 'src/utils/useAlertMessages';
import { formatPlainTextToHtml } from 'src/utils/helpers';
import { MESSAGES } from 'src/utils/errorMessages';
import { passdownHistoryStyles } from 'src/components/shared/design-tokens';
import { requiredMark, requiredFieldHint } from 'src/components/shared/formHelpers';
import mockData from 'src/mock/mockData.json';

const DRAFT_KEY = 'passdown-events';
const USE_MOCK_DATA = false;

interface EventsFormData {
    category: string;
    eventsGeneralUpdates: string;
    eventsCaseGradingComplete: boolean;
    eventsCaseGradingNotes: string;
    eventsCaseReviews: string;
    // Tiptap ProseMirror JSON mirrors of the Case Grading Notes and 1:1 Case
    // Reviews rich-text fields.
    eventsCaseGradingNotesJson?: Record<string, any>;
    eventsCaseReviewsJson?: Record<string, any>;
}

const defaultForm: EventsFormData = {
    category: 'EVENTS',
    eventsGeneralUpdates: '',
    eventsCaseGradingComplete: false,
    eventsCaseGradingNotes: '',
    eventsCaseReviews: '',
};

/** Strips HTML tags to test whether rich-text content is actually present. */
const richTextToPlain = (html?: string) => (html || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();

const EventsPassdown = ({ showForm, setShowForm }: { showForm: boolean; setShowForm: (v: boolean) => void }) => {
    const { canModify } = useUser();
    const [passdowns, setPassdowns] = useState<ShiftPassdownEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [activeExpanded, setActiveExpanded] = useState(true);
    const [showAllHistory, setShowAllHistory] = useState(false);
    const [form, setForm] = useState<EventsFormData>({ ...defaultForm });
    const { error, successMsg, setError, setSuccessMsg, alertRef } = useAlertMessages();
    const [deleteTarget, setDeleteTarget] = useState<{ id: string; createdBy: string } | null>(null);

    const updateField = (field: keyof EventsFormData, value: string | boolean | Record<string, any>) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    useEffect(() => {
        loadPassdowns();
        const draft = loadDraft(DRAFT_KEY);
        if (draft) {
            setForm({ ...defaultForm, ...draft });
            setEditingId(draft.editingId || null);
            setShowForm(true);
            clearDraft(DRAFT_KEY);
        }
    }, []);

    const loadPassdowns = async () => {
        setLoading(true);
        try {
            if (USE_MOCK_DATA) {
                setPassdowns((mockData.eventspassdowns?.items || []) as ShiftPassdownEntry[]);
            } else {
                const data = await fetchShiftPassdown('EVENTS');
                setPassdowns(data.items || []);
            }
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (b: ShiftPassdownEntry) => {
        setEditingId(b.passdownId);
        setForm({
            category: 'EVENTS',
            eventsGeneralUpdates: b.eventsGeneralUpdates || '',
            eventsCaseGradingComplete: b.eventsCaseGradingComplete || false,
            eventsCaseGradingNotes: b.eventsCaseGradingNotes || '',
            eventsCaseReviews: b.eventsCaseReviews || '',
            eventsCaseGradingNotesJson: b.eventsCaseGradingNotesJson,
            eventsCaseReviewsJson: b.eventsCaseReviewsJson,
        });
        setShowForm(true);
    };

    const resetForm = () => {
        setShowForm(false);
        setEditingId(null);
        setForm({ ...defaultForm });
        clearDraft(DRAFT_KEY);
    };

    const handleDeleteClick = (b: ShiftPassdownEntry) => {
        setDeleteTarget({ id: b.passdownId, createdBy: b.createdBy });
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        try {
            await deleteShiftPassdown(deleteTarget.id);
            setSuccessMsg('Events Passdown deleted');
            await loadPassdowns();
        } catch (err: any) {
            setError(MESSAGES.RECORD_DELETE_ERROR);
        }
        setDeleteTarget(null);
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            const input: CreateShiftPassdownInput = { ...form };
            const isUpdate = !!editingId;
            if (editingId) { await updateShiftPassdown(editingId, input); }
            else { await createShiftPassdown(input); }
            resetForm();
            setSuccessMsg(isUpdate ? 'Events Passdown updated' : 'Events Passdown created');
            await loadPassdowns();
        } catch (err: any) {
            const msg = err.message || '';
            if (msg.includes('Not authenticated') || msg.includes('403')) {
                saveDraft(DRAFT_KEY, { ...form, editingId });
            }
            setError(editingId ? MESSAGES.RECORD_UPDATE_ERROR : MESSAGES.RECORD_CREATE_ERROR);
        } finally { setSubmitting(false); }
    };

    // Render a Case Grading Notes / 1:1 Case Reviews field read-only. Prefers the
    // Tiptap JSON when present (rendered safely via RichTextContent); otherwise
    // falls back to the legacy plain-text string so older records still render.
    const renderJsonField = (label: string, json?: Record<string, any>, value?: string) => {
        // `value` is the plain-text mirror of the JSON (both are emitted together by
        // the editor and co-persisted), so it reliably reflects whether the field has
        // real content — including when the editor was cleared to an empty document
        // (which still emits a JSON doc). Hide empty fields, matching the original
        // renderField behavior.
        if (!richTextToPlain(value)) return null;
        return (
            <div className="passdown-entry__field">
                <h3>{label}</h3>
                <div className="passdown-rich-text">
                    <RichTextContent json={json as any} html={formatPlainTextToHtml(value || '')} />
                </div>
            </div>
        );
    };

    const renderRichField = (label: string, html?: string) => {
        if (!richTextToPlain(html)) return null;
        return (
            <div className="passdown-entry__field">
                <h3>{label}</h3>
                <div className="passdown-rich-text"><RichTextContent html={html} /></div>
            </div>
        );
    };

    const renderEntryFields = (pd: ShiftPassdownEntry) => (
        <div className="passdown-entry__fields-grid">
            {renderRichField('General Updates', pd.eventsGeneralUpdates)}
            <div className="passdown-entry__field">
                <h3>Case Grading Complete?</h3>
                <p>{pd.eventsCaseGradingComplete ? 'Yes' : 'No'}</p>
            </div>
            {renderJsonField('Case Grading Notes', pd.eventsCaseGradingNotesJson, pd.eventsCaseGradingNotes)}
            {renderJsonField('1:1 Case Reviews/Remediations', pd.eventsCaseReviewsJson, pd.eventsCaseReviews)}
        </div>
    );

    // General Updates and Case Reviews are the required substantive fields.
    const hasContent = !!richTextToPlain(form.eventsGeneralUpdates) && !!richTextToPlain(form.eventsCaseReviews);
    const b0 = passdowns[0];

    return (
        <>
            <div ref={alertRef} tabIndex={-1}>
                { error &&
                    <div className="alert">
                        <Alert
                            dismissible
                            type="error"
                            onDismiss={() => setError(null)}
                        >
                            <strong>Error: </strong>{error}
                        </Alert>
                    </div>
                }
                { successMsg &&
                    <div className="alert">
                        <Alert
                            dismissible
                            type="success"
                            onDismiss={() => setSuccessMsg(null)}
                        >
                            {successMsg}
                        </Alert>
                    </div>
                }
            </div>

            {showForm && (
                <div className="passdown-form">
                    <FormField label={<>General Updates{requiredMark()}</>} stretch>
                        <RichTextEditor
                            key={editingId || 'new'}
                            value={form.eventsGeneralUpdates}
                            placeholder="General updates..."
                            onChange={(html) => updateField('eventsGeneralUpdates', html)}
                        />
                    </FormField>

                    <Checkbox
                        checked={form.eventsCaseGradingComplete}
                        onChange={({ detail }) => updateField('eventsCaseGradingComplete', detail.checked)}
                    >
                        <span style={{ fontWeight: 600 }}>Case grading complete?</span>
                    </Checkbox>

                    <FormField label="Case Grading Notes" stretch>
                        <RichTextEditor
                            key={editingId || 'new'}
                            value={form.eventsCaseGradingNotesJson ?? formatPlainTextToHtml(form.eventsCaseGradingNotes || '')}
                            placeholder="Notes on case grading..."
                            onChangeText={(text) => updateField('eventsCaseGradingNotes', text)}
                            onChangeJson={(json) => updateField('eventsCaseGradingNotesJson', json)}
                        />
                    </FormField>

                    <FormField label={<>1:1 Case Reviews/Remediations{requiredMark()}</>} stretch>
                        <RichTextEditor
                            key={editingId || 'new'}
                            value={form.eventsCaseReviewsJson ?? formatPlainTextToHtml(form.eventsCaseReviews || '')}
                            placeholder="1:1 case reviews and remediations..."
                            onChangeText={(text) => updateField('eventsCaseReviews', text)}
                            onChangeJson={(json) => updateField('eventsCaseReviewsJson', json)}
                        />
                    </FormField>

                    <div className="passdown-form__actions">
                        {requiredFieldHint()}
                        <div className="passdown-form__actions-buttons">
                            <Button variant="link" onClick={resetForm}>Cancel</Button>
                            <Button
                                variant="primary"
                                onClick={handleSubmit}
                                disabled={submitting || !hasContent}
                            >
                                {submitting ? 'Submitting...' : (editingId ? 'Update Events Passdown' : 'Submit Events Passdown')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {loading && <Spinner />}
            {!loading && passdowns.length === 0 && !showForm && <p className="section-card__placeholder">No Events passdowns yet.</p>}

            {!loading && b0 && (
                <>
                    <div className="passdown-list">
                        <div className="passdown-entry">
                            <div className="passdown-entry__icon">
                                <span className="passdown-entry__meta">
                                    <FormattedDate date={b0.createdAt} /> &middot; by {b0.createdBy}
                                    <span className="badge-custom">
                                        <Badge color="green">Active</Badge>
                                    </span>
                                    { b0.updatedAt && b0.updatedAt !== b0.createdAt && (
                                        <span className="passdown-entry__last-updated-info">Last updated on <FormattedDate date={b0.updatedAt} /> &middot; by {b0.updatedBy}</span>
                                    )}
                                </span>
                                <div className="passdown-entry__actions">
                                    {canModify('eventsShiftPassdown', b0.createdBy) && (
                                        <ActionButtons
                                            onEdit={() => handleEdit(b0)}
                                            onDelete={() => handleDeleteClick(b0)}
                                            disabled={showForm}
                                        />
                                    )}
                                    <span className="passdown-history__toggle" onClick={() => setActiveExpanded(prev => !prev)}>
                                        <Icon
                                            name={activeExpanded ? "caret-up-filled" : "caret-down-filled"}
                                            variant="subtle"
                                        />
                                    </span>
                                </div>
                            </div>

                            {!activeExpanded && <p style={{marginTop: "8px"}}>{richTextToPlain(b0.eventsGeneralUpdates) || richTextToPlain(b0.eventsCaseReviews)} ...</p>}

                            {activeExpanded && renderEntryFields(b0)}
                        </div>
                    </div>

                    {passdowns.length > 1 && (
                        <div className="passdown-history">
                            <h4 className="passdown-history__title">📋 EVENTS PASSDOWN HISTORY</h4>
                            <div className="passdown-history__list">
                                {(showAllHistory ? passdowns.slice(1) : passdowns.slice(1, 6)).map((item, idx) => {
                                    const isExpanded = expandedId === item.passdownId;
                                    return (
                                        <span key={item.passdownId} style={{marginBottom: "0.8rem"}}>
                                            <Container style={passdownHistoryStyles}>
                                                <span className="passdown-history__dot" />
                                                <div className="passdown-history__content">
                                                    <div className="passdown-history__top">
                                                        <span className="passdown-history__date">
                                                            <FormattedDate date={item.createdAt} /> &middot; by {item.createdBy}
                                                            { item.updatedAt && item.updatedAt !== item.createdAt && (
                                                                <span className="passdown-entry__last-updated-info">Last updated on <FormattedDate date={item.updatedAt} /> &middot; by {item.updatedBy}</span>
                                                            )}
                                                        </span>
                                                        <div className="passdown-history__top-right">
                                                            {canModify('eventsShiftPassdown', item.createdBy) && (
                                                                <ActionButtons
                                                                    onEdit={() => handleEdit(item)}
                                                                    onDelete={() => handleDeleteClick(item)}
                                                                    disabled={showForm}
                                                                />
                                                            )}
                                                            <span className="passdown-history__toggle" onClick={() => setExpandedId(isExpanded ? null : item.passdownId)}>
                                                                <Icon
                                                                    name={isExpanded ? "caret-up-filled" : "caret-down-filled"}
                                                                    variant="subtle"
                                                                />
                                                            </span>
                                                        </div>
                                                    </div>
                                                    {isExpanded && (
                                                        <div className="passdown-history__details">
                                                            {renderEntryFields(item)}
                                                        </div>
                                                    )}
                                                </div>
                                            </Container>
                                        </span>
                                    );
                                })}
                            </div>
                            {passdowns.length > 6 && (
                                <div data-size='sm'>
                                    <Button
                                        variant="inline-link"
                                        ariaLabel={showAllHistory ? 'view less' : 'view more'}
                                        onClick={() => setShowAllHistory(prev => {
                                            if (prev) {
                                                setTimeout(() => alertRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
                                            }
                                            return !prev;
                                        })}
                                    >
                                        {showAllHistory ? 'View less' : 'View more'}
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}

            {deleteTarget && (
                <Modal
                    visible
                    header="Delete Events Passdown"
                    onDismiss={() => setDeleteTarget(null)}
                    footer={
                        <Box float="right">
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button variant="link" onClick={() => setDeleteTarget(null)}>Cancel</Button>
                                <Button variant="primary" onClick={confirmDelete}>Delete</Button>
                            </SpaceBetween>
                        </Box>
                    }
                >
                    Are you sure you want to delete this Events passdown by <strong>{deleteTarget.createdBy}</strong>? This cannot be undone.
                </Modal>
            )}
        </>
    );
};

export default EventsPassdown;
